<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Notifications</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Notifications List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="notifications"
      :fetch="fetchNotifications"
      :classes="{
        td: Array(8)
          .fill(0)
          .map(() => 'max-w-xs truncate'),
      }"
    >
      <template #table-toolbar>
        <button
          type="button"
          :disabled="!$canI.create"
          class="shared-table-toolbar-button"
          @click="showFormModal = true"
        >
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Notification
        </button>
      </template>

      <template #table-data-actions="trData">
        <td class="shared-table-cells-cell">
          <div class="flex justify-end">
            <button
              v-if="!trData.is_publish"
              :disabled="!$canI.update"
              class="mr-2 shared-table-cells-button round send"
              @click="() => handlePublish(trData.id)"
            >
              <VSvgIcon name="send" class="h-5" />
            </button>
            <button
              :disabled="!$canI.update"
              class="mr-2 shared-table-cells-button round edit"
              @click="() => handleSelectEditableNotification(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button
              :disabled="!$canI.delete"
              class="shared-table-cells-button round delete"
              @click="() => handleDeleteNotification(trData.id)"
            >
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Modals -->
    <ModalPlus
      maxWidth="700px"
      :title="editable ? 'Edit Notification' : 'Add Notification'"
      :buttonText="editable ? 'Save' : 'Add'"
      :show="showFormModal"
      :loading="formLoading"
      :onClose="handleClose"
      :onProceed="handleSubmit(handleProceed)"
    >
      <div class="py-8 pb-4">
        <NotificationsForm
          :mediums="mediums"
          :errors="errors"
          :initialValues="editableNotification"
          @set="(name, value) => handleSetField(name, value)"
        />
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { format, parseISO } from 'date-fns';

import VSvgIcon from '@/components/base/VSvgIcon';
import Table from '@/components/shared/table/Table';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import NotificationsForm from '@/components/admin/NotificationsForm';

import { formMixin } from '@/mixins';

import { safeNullDataset, setTranslationColumn } from '@/utils';

const MEDIUMS = [
  { name: 'portal', value: 'PORTAL' },
  { name: 'email', value: 'EMAIL' },
  { name: 'both', value: 'BOTH' },
];

export default {
  name: 'AdminNotifications',
  components: { VSvgIcon, Table, ModalPlus, NotificationsForm },
  mixins: [
    formMixin(
      {
        medium: '',
        title: '',
        content: '',
        link_text: '',
        link_url: '',
        is_external: false,
      },
      ['link_text', 'link_url', 'is_external'],
    ),
  ],
  data() {
    return {
      mediums: MEDIUMS,
      editableNotification: {},
      showFormModal: false,
    };
  },
  computed: mapState({
    notifications: state => ({
      data: safeNullDataset(state)(['admin', 'notifications', 'data']).map(n => ({
        "it's translation": setTranslationColumn(n) || '-',
        title: n.title || '-',
        content: n.content || '-',
        medium: n.medium || '-',
        'link text': n.link_text || '-',
        'link url': n.link_url || '-',
        status: n.is_publish ? 'published' : 'draft',
        'date created': format(parseISO(n.created_at), 'MM/dd/yyyy') || '-',
        ...n,
      })),
      meta: state.admin.notifications.meta,
    }),
    editable() {
      return !!Object.keys(this.editableNotification).length;
    },
  }),
  methods: {
    handleClose() {
      this.editableNotification = {};
      this.showFormModal = false;
      this.handleResetFields();
    },
    async handleProceed(data) {
      const { code: lang_code } = this.$language;
      !this.editable
        ? await this.handleAddNotification({ ...data, lang_code })
        : await this.handleEditNotification({ ...data, lang_code });
      this.handleClose();
    },
    async handleEditNotification(data) {
      const notificationId = this.editableNotification.id;
      return await this.$store.dispatch('admin/updateNotification', { notificationId, data });
    },
    async handleAddNotification(data) {
      return await this.$store.dispatch('admin/addNotification', { data });
    },
    async handlePublish(id) {
      return await this.$store.dispatch('admin/publishNotification', id);
    },
    async fetchNotifications(params) {
      return await this.$store.dispatch('admin/getNotifications', params);
    },
    handleSelectEditableNotification(data) {
      this.showFormModal = true;
      this.editableNotification = data;
    },
    handleDeleteNotification(id) {
      this.$store.dispatch('admin/deleteNotification', id);
    },
  },
  async mounted() {
    this.$store.dispatch('admin/getNotifications', { per_page: 15 });
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: "it's translation", isSort: false },
      { column: 'title', isSort: true, orderBy: 'title' },
      { column: 'content', isSort: true, orderBy: 'content' },
      { column: 'medium', isSort: true, orderBy: 'medium' },
      { column: 'link text', isSort: true, orderBy: 'link_text' },
      { column: 'link url', isSort: true, orderBy: 'link_url' },
      { column: 'status', isSort: true, orderBy: 'is_publish' },
      { column: 'date created', isSort: true, orderBy: 'created_at' },
      { column: 'actions', isSort: false },
    ];
  },
};
</script>
